/* eslint-disable no-param-reassign */
import type { Dispatch, SetStateAction } from 'react';
import type { CardElementProps } from '@adyen/adyen-web/dist/types/components/Card/types';
import type { PaymentContextType } from '../../../context/paymentContext';
import { colours } from '../../../stylings';
import type { PlaceOrderMutationFn } from '../../../types';
import { type PaymentMethodTypes, isCreditOrDebitCardPayment } from '../../../utils/adyen';
import { newRelicAvailabilityChecker } from '../../../utils/newrelic';
import type { CartAddress } from '../useAdyenConfig';

// Adyen docs - web components - cards
// https://docs.adyen.com/payment-methods/cards/web-component/

export interface CreateCardConfigProps {
  paymentData: PaymentContextType['paymentData'];
  setIsPaymentBtnDisabled: Dispatch<SetStateAction<boolean>>;
  setPaymentInProgress: Dispatch<SetStateAction<boolean>>;
  setPaymentError: Dispatch<SetStateAction<boolean>>;
  placeOrderMutation: PlaceOrderMutationFn;
  resolveData: (data: CartAddress) => Promise<CartAddress>;
}

export const createCardConfig = ({
  paymentData,
  setIsPaymentBtnDisabled,
  setPaymentInProgress,
  placeOrderMutation,
  setPaymentError,
  resolveData,
}: CreateCardConfigProps): CardElementProps => ({
  hasHolderName: true,
  holderNameRequired: true,
  positionHolderNameOnTop: true,
  showBrandIcon: true,
  showPayButton: false,
  showFormInstruction: false,
  brands: paymentData?.paymentMethods?.find(({ type }) => isCreditOrDebitCardPayment(type as PaymentMethodTypes))
    ?.brands as string[],
  styles: {
    placeholder: {
      padding: '0',
      color: colours.BLACK,
      fontFamily: 'Montserrat, sans-serif',
    },
  },
  beforeSubmit: async (data, _, actions) => {
    try {
      setIsPaymentBtnDisabled(true);
      setPaymentInProgress(true);

      const result = await placeOrderMutation();

      if (result?.data?.placeOrder.accepted) {
        actions.resolve(await resolveData(data));
      } else {
        actions.reject();
        setPaymentError(true);
        setIsPaymentBtnDisabled(false);
        setPaymentInProgress(false);
      }
    } catch (error) {
      actions.reject();
      setPaymentError(true);
      setIsPaymentBtnDisabled(false);
      setPaymentInProgress(false);
      console.error('Error during Credit or Debit Card payment processing:', error);
      newRelicAvailabilityChecker(() => {
        window.newrelic.noticeError(new Error(`Error during Credit or Debit payment processing: ${error}`));
      });
    }
  },
});
